import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navbar, Nav, Container, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../actions/userActions';
import api from '../api/axios';
import { Menu } from 'lucide-react';
import { SidebarIcon, LockIcon } from './sidebarIcons';

function SideBar() {
  const [collapsed, setCollapsed] = useState(false);
  const [, setWindowWidth] = useState(window.innerWidth);
  const previousWidthRef = useRef(window.innerWidth);
  const [showLabels, setShowLabels] = useState(!(window.innerWidth < 992));

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const userDetails = useSelector((state) => state.userDetails);
  const { user } = userDetails;

  const subscription = user?.subscription || userInfo?.subscription;

  const hasMainAccess = user?.has_main_access ?? userInfo?.has_main_access ?? false;
  const hasExpertAccess = user?.has_expert_access ?? userInfo?.has_expert_access ?? false;

  const isFreeAccount = !hasMainAccess;

  const userType = user?.user_type || userInfo?.user_type || 'worker';
  const isAdultWorker = userType === 'adult_worker';
  
  const logoutHandler = () => {
    dispatch(logout());
    navigate('/login');
  };
    const [pendingInvites, setPendingInvites] = useState(0);

  const fetchPendingInvites = useCallback(async () => {
    try {
      const res = await api.get('/vacation/link-requests/');   // było: /worker/link-requests/
      const count = (res.data || []).filter(l => l.status === 'pending').length;
      setPendingInvites(count);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchPendingInvites();

    const handler = () => fetchPendingInvites();
    window.addEventListener('invitesUpdated', handler);
    return () => window.removeEventListener('invitesUpdated', handler);
  }, [fetchPendingInvites, location]);
  
  const toggleSidebar = () => {
    setCollapsed((prev) => !prev);
  };

  useEffect(() => {
    if (window.innerWidth < 992) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location]);

  useEffect(() => {
    if (window.innerWidth < 992) {
      setCollapsed(true);
    } else {
      setCollapsed(false);
    }

    const handleResize = () => {
      const currentWidth = window.innerWidth;
      setWindowWidth(currentWidth); 

      if (currentWidth < 992 && previousWidthRef.current >= 992) {
        setCollapsed(true);
      } else if (currentWidth >= 992 && previousWidthRef.current < 992) {
        setCollapsed(false);
      }
      previousWidthRef.current = currentWidth;
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleNavClick = () => {
    if (window.innerWidth < 992) {
      setTimeout(() => {
        setCollapsed(true);
      }, 50);
    }
  };

  useEffect(() => {
    let timeout;
    if (!collapsed) {
      timeout = setTimeout(() => setShowLabels(true), 300);
    } else {
      setShowLabels(false);
    }
    return () => clearTimeout(timeout);
  }, [collapsed]);

  return (
    <>
      <Navbar
        className="app-sidebar"
        style={{ 
          width: collapsed ? '70px' : '200px',
          height: '100vh',
          position: 'fixed',
          transition: 'width 0.3s ease',
          zIndex: 1000,
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
      >
        <Container className="d-flex flex-column h-100 p-0">
          <div className="d-flex align-items-center ps-3 py-3 border-bottom">
            <button 
              onClick={toggleSidebar}
              className="btn btn-link p-0 me-2 app-sidebar-toggle"
              aria-label="Zwiń lub rozwiń menu"
              style={{ fontSize: '1.25rem' }}
            >
              <Menu size={20} strokeWidth={1.75} />
            </button>
            {!collapsed && (
              <Navbar.Brand 
                as={Link} 
                to={isAdultWorker ? '/link-requests' : '/home-screen'}
                className="mb-0 d-flex align-items-center" 
                style={{ paddingLeft: 8 }}
                onClick={handleNavClick}
              >
                <span className="pub-logo">SIGMA</span>
              </Navbar.Brand>
            )}
          </div>

          {isFreeAccount && !collapsed && (
            <div className="px-3 py-2">
              <div className="app-sidebar-plan">Konto darmowe</div>
            </div>
          )}

          <Nav className="flex-column flex-grow-1 mt-2">
            {!isAdultWorker && (
              <NavItem 
                to="/home-screen"
                icon="fa-house"
                text="Strona główna"
                collapsed={collapsed}
                showLabels={showLabels}
                isLocked={false}
                onNavClick={handleNavClick}
              />
            )}

            <NavItem 
              to="/link-requests"
              icon="fa-user-tie"
              text="Zaproszenia od pracodawcy"
              collapsed={collapsed}
              showLabels={showLabels}
              isLocked={false}
              onNavClick={handleNavClick}
              badgeCount={pendingInvites}
            />

            {!isAdultWorker && (
              <>
                <NavItem 
                  to="/vacations"
                  icon="fa-umbrella-beach"
                  text="Mój urlop"
                  collapsed={collapsed}
                  showLabels={showLabels}
                  isLocked={false}
                  onNavClick={handleNavClick}
                />

                <NavItem 
                  to="/employment"
                  icon="fa-building"
                  text="Pracodawca"
                  collapsed={collapsed}
                  showLabels={showLabels}
                  isLocked={false}
                  onNavClick={handleNavClick}
                />
                
                <NavItem 
                  to="/salaries"
                  icon="fa-money-bill-wave"
                  text="Stawki wynagrodzenia"
                  collapsed={collapsed}
                  showLabels={showLabels}
                  isLocked={false}
                  onNavClick={handleNavClick}
                />

                <NavItem 
                  to="/rights"
                  icon="fa-scale-balanced"
                  text="Prawa i obowiązki w pigułce"
                  collapsed={collapsed}
                  showLabels={showLabels}
                  isLocked={!hasMainAccess}
                  onNavClick={handleNavClick}
                />

                <NavItem 
                  to="/cv-career"
                  icon="fa-file-signature"
                  text="CV & start kariery"
                  collapsed={collapsed}
                  showLabels={showLabels}
                  isLocked={!hasMainAccess}
                  onNavClick={handleNavClick}
                />

                <NavItem 
                  to="/communication"
                  icon="fa-comments"
                  text="Komunikacja"
                  collapsed={collapsed}
                  showLabels={showLabels}
                  isLocked={!hasMainAccess}
                  onNavClick={handleNavClick}
                />
                
                <NavItem 
                  to="/changes"
                  icon="fa-clipboard-list"
                  text="Więcej o zmianach"
                  collapsed={collapsed}
                  showLabels={showLabels}
                  isLocked={!hasMainAccess}
                  onNavClick={handleNavClick}
                />

                <NavItem 
                  to="/ask-expert"
                  icon="fa-lightbulb"
                  text="Zapytaj eksperta"
                  collapsed={collapsed}
                  showLabels={showLabels}
                  isLocked={!hasExpertAccess}
                  onNavClick={handleNavClick}
                />

              </>
            )}
          </Nav>

          <div style={{ 
            width: '100%', 
            paddingBottom: window.innerWidth < 992 ? '100px' : '0px', 
            marginTop: 'auto', 
            display: 'flex', 
            flexDirection: 'column' 
          }}>
            <Nav className="flex-column">
              {!isAdultWorker && (
                <NavItem 
                  to="/app-pricing"
                  icon="fa-line-chart"
                  text="Plan subskrypcji"
                  collapsed={collapsed}
                  showLabels={showLabels}
                  isLocked={false}
                  onNavClick={handleNavClick}
                />
              )}

              <NavItem 
                to="/user-profile"
                icon="fa-circle-user"
                text="Mój profil"
                collapsed={collapsed}
                showLabels={showLabels}
                isLocked={false}
                onNavClick={handleNavClick}
              />

              <NavItem 
                icon="fa-right-from-bracket"
                text="Wyloguj się"
                collapsed={collapsed}
                showLabels={showLabels}
                onClick={logoutHandler}
                isLocked={false}
                onNavClick={handleNavClick}
              />
            </Nav>
          </div>
        </Container>
      </Navbar>

      <div style={{
        marginLeft: collapsed ? '70px' : '200px',
        transition: 'margin-left 0.3s ease',
        minHeight: '100vh'
      }}>
      </div>
    </>
  );
}

function NavItem({ to, icon, text, collapsed, showLabels, onClick, isLocked = false, onNavClick, badgeCount = 0 }) {
  const { pathname } = useLocation();
  const isActive = !!to && pathname === to;
  const isMobile = window.innerWidth < 992;

  const handleClick = (e) => {
    if (isLocked) {
      e.preventDefault();
      return;
    }

    if (onClick) onClick(e);
    if (onNavClick) onNavClick();

    if (collapsed) {
      setTimeout(() => {
        document.activeElement?.blur();
      }, 100);
    }
  };

  const content = (
    <span style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
      <span style={{ position: 'relative', display: 'inline-block' }}>
        <SidebarIcon name={icon} dimmed={isLocked} />
        {badgeCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '-6px',
              right: collapsed ? '-8px' : '2px',
              background: '#e03131',
              color: 'white',
              borderRadius: '50%',
              fontSize: '10px',
              fontWeight: 700,
              minWidth: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 3px',
              lineHeight: 1,
            }}
          >
            {badgeCount > 9 ? '9+' : badgeCount}
          </span>
        )}
      </span>
      {showLabels && (
        <span
          style={{
            marginLeft: 12,
            position: 'relative',
            display: 'inline-block',
            minWidth: 0,
            wordBreak: 'break-word',
            flex: 1,
            opacity: 0,
            transform: 'translateX(-5px)',
            animation: showLabels ? 'slideIn 0.1s ease-out forwards' : 'none'
          }}
        >
          <style>{`
            @keyframes slideIn {
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
          `}</style>
          {text}
          {isLocked && showLabels && (
            <span className="ms-2 app-sidebar-lock"><LockIcon /></span>
          )}
        </span>
      )}
    </span>
  );

  // reszta funkcji (linkProps, tooltipText, showTooltip, return) bez zmian

  const linkProps = {
    className: `${collapsed ? "py-3" : "px-3 py-3"}${isActive ? " active" : ""}`,
    onClick: handleClick,
    style: { 
      opacity: isLocked ? 0.6 : 1,
      cursor: isLocked ? 'not-allowed' : 'pointer',
      display: 'flex',
      justifyContent: collapsed ? 'center' : 'flex-start'
    }
  };

  const tooltipText = isLocked ? "Dostępne tylko w płatnej wersji" : text;
  const showTooltip = isLocked || collapsed;

  if (isMobile) {
    return to ? (
      <Nav.Link as={isLocked ? 'div' : Link} to={isLocked ? undefined : to} {...linkProps}>
        {content}
      </Nav.Link>
    ) : (
      <Nav.Link {...linkProps}>{content}</Nav.Link>
    );
  }

  return (
    <OverlayTrigger
      placement="right"
      overlay={<Tooltip>{tooltipText}</Tooltip>}
      show={showTooltip ? undefined : false}
    >
      {to ? (
        <Nav.Link as={isLocked ? 'div' : Link} to={isLocked ? undefined : to} {...linkProps}>
          {content}
        </Nav.Link>
      ) : (
        <Nav.Link {...linkProps}>{content}</Nav.Link>
      )}
    </OverlayTrigger>
  );
}

export default SideBar;